import React, { useState, useEffect, useRef } from "react";

const EASE_OUT = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Counts from 0 to `value` the first time it scrolls into view.
 * Non-numeric stats (e.g. "24/7") should be passed as `display` instead,
 * in which case the value is rendered as-is.
 */
function Counter({ value, display, prefix = "", suffix = "", duration = 1600 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (display != null) return;

    const node = ref.current;
    if (!node) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(value);
      return;
    }

    let frame = 0;
    let start = 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        const step = (now) => {
          if (!start) start = now;
          const progress = Math.min((now - start) / duration, 1);
          setShown(Math.round(EASE_OUT(progress) * value));
          if (progress < 1) frame = requestAnimationFrame(step);
        };

        frame = requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [value, duration, display]);

  return (
    <span ref={ref} aria-label={display != null ? undefined : `${prefix}${value}${suffix}`}>
      {display != null ? (
        display
      ) : (
        <>
          {prefix}
          {shown.toLocaleString()}
          {suffix}
        </>
      )}
    </span>
  );
}

export default Counter;
